<template>
  <div class="card">
    <div class="card-header pb-0 d-flex justify-content-between align-items-center">
      <div>
        <h6>直购出奖明细</h6>
        <p class="text-xs text-secondary">逐笔出奖记录，含出货状态与补偿追踪</p>
      </div>
      <button class="btn btn-sm btn-outline-info" @click="fetchData"><i class="ni ni-refresh"></i> 刷新</button>
    </div>
    <div class="card-body px-0 pt-0 pb-2">
      <div v-if="loading" class="text-center text-secondary text-xs py-4">加载中...</div>
      <div v-else-if="records.length === 0" class="text-center text-secondary text-xs py-4">暂无数据</div>
      <div v-else>
        <!-- 汇总统计 -->
        <div class="d-flex flex-wrap gap-3 px-3 pb-2">
          <span class="badge bg-info">总笔数: {{ totalCount }}</span>
          <span class="badge bg-success">成功出货: {{ successCount }}</span>
          <span class="badge bg-danger">出货失败: {{ failCount }}</span>
        </div>
        <!-- 明细列表 -->
        <div class="table-responsive p-0">
          <table class="table table-sm align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-xs text-secondary">订单号</th>
                <th class="text-xs text-secondary">设备ID</th>
                <th class="text-xs text-secondary">货道</th>
                <th class="text-xs text-secondary text-end">金额</th>
                <th class="text-xs text-secondary text-center">状态</th>
                <th class="text-xs text-secondary text-center">补偿</th>
                <th class="text-xs text-secondary text-end">时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in displayRecords" :key="item.order_no">
                <td class="text-xs font-monospace">{{ item.order_no }}</td>
                <td class="text-xs font-monospace">{{ item.board_id }}</td>
                <td class="text-xs">{{ item.channel_idx }}</td>
                <td class="text-xs text-end">¥{{ formatMoney(item.fee) }}</td>
                <td class="text-center">
                  <span :class="stateClass(item)" class="badge text-xs">{{ stateText(item) }}</span>
                </td>
                <td class="text-center">
                  <span v-if="compType(item)" class="badge bg-warning text-dark text-xs">{{ compType(item) }}</span>
                  <span v-else class="text-xs text-secondary">-</span>
                </td>
                <td class="text-xs text-end">{{ formatDate(item.created_date) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- 分页 -->
        <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
          <nav>
            <ul class="pagination pagination-sm">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="javascript:;" @click="currentPage--">上一页</a>
              </li>
              <li class="page-item active"><span class="page-link">{{ currentPage }}/{{ totalPages }}</span></li>
              <li class="page-item" :class="{ disabled: currentPage >= totalPages }">
                <a class="page-link" href="javascript:;" @click="currentPage++">下一页</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDirectGifts } from "@/api/dashboard";

export default {
  name: "DirectGiftReport",
  props: {
    deliverId: { type: Number, default: 0 },
    startDate: { type: Number, required: true },
    endDate: { type: Number, required: true },
  },
  data() {
    return {
      records: [],
      totalCount: 0,
      loading: false,
      currentPage: 1,
      pageSize: 15,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.records.length / this.pageSize);
    },
    displayRecords() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.records.slice(start, start + this.pageSize);
    },
    successCount() {
      return this.records.filter((r) => r.exec_result === 0).length;
    },
    failCount() {
      return this.records.filter((r) => r.exec_result !== null && r.exec_result !== undefined && r.exec_result !== 0).length;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatMoney(fen) {
      if (!fen) return "0.00";
      return (fen / 100).toFixed(2);
    },
    formatDate(ts) {
      if (!ts) return "-";
      const d = new Date(ts * 1000);
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    stateText(item) {
      if (item.exec_result === 0) return "成功";
      if (item.exec_result === null || item.exec_result === undefined) return "待处理";
      return "失败";
    },
    stateClass(item) {
      if (item.exec_result === 0) return "bg-success";
      if (item.exec_result === null || item.exec_result === undefined) return "bg-warning text-dark";
      return "bg-danger";
    },
    compType(item) {
      if (!item.gift_type) return "";
      return item.gift_type;
    },
    async fetchData() {
      this.loading = true;
      this.currentPage = 1;
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate,
          page: 1,
          page_size: 10000,
        };
        if (this.deliverId > 0) params.deliver_id = this.deliverId;
        const resp = await getDirectGifts(params);
        if (resp.code === 0 && resp.desc) {
          let records = [];
          if (Array.isArray(resp.desc)) {
            records = resp.desc;
          } else if (resp.desc.records) {
            records = resp.desc.records;
          }
          // 过滤掉可能残留的 TEST 开头的测试订单（双保险，后端已过滤）
          this.records = records.filter(r => r.order_no && !r.order_no.startsWith('TEST'));
          this.totalCount = this.records.length;
        }
      } catch (err) {
        console.error("获取直购出奖失败:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
